import React from 'react';
import { CodeBlock } from '@/components/docs/CodeBlock';

export default function FixturePage() {
  return (
    <article className="max-w-3xl" data-testid="docs-fixture">
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">Fixture Prop</h1>
      <p className="text-base text-white/50 leading-relaxed mb-8">Provide mock content for components that depend on API data or authentication.</p>

      <h2 className="text-xl font-bold tracking-tight mb-4">The problem</h2>
      <p className="text-sm text-white/40 mb-6 leading-relaxed">When the CLI visits your app, some components may not render because they depend on API responses or user sessions. Without visible content, the CLI can't extract layout bones.</p>

      <h2 className="text-xl font-bold tracking-tight mb-4">The solution</h2>
      <p className="text-sm text-white/40 mb-4 leading-relaxed">Pass a <code className="text-white/60 bg-white/[0.06] px-1.5 py-0.5 rounded text-xs">fixture</code> prop with mock content. During CLI capture, the fixture is rendered instead of children:</p>
      <CodeBlock filename="app/dashboard/page.tsx" code={`<Skeleton
  name="dashboard-stats"
  loading={isLoading}
  fixture={
    <DashboardStats data={{
      users: 12345,
      revenue: "$45,678",
      orders: 1234,
      growth: "32.5%"
    }} />
  }
>
  {data && <DashboardStats data={data} />}
</Skeleton>`} />

      <div className="border border-amber-500/20 bg-amber-500/[0.04] rounded-lg p-5 mt-8 mb-8">
        <p className="text-sm text-amber-400/80 font-medium mb-1">Important</p>
        <p className="text-sm text-white/40">The fixture is <strong className="text-white/60">only rendered during CLI capture</strong> (when the URL contains <code className="text-white/60 bg-white/[0.06] px-1 py-0.5 rounded text-xs">?ghostlayout-extract=true</code>). In production, it's never rendered — only the skeleton or the real children.</p>
      </div>

      <h2 className="text-xl font-bold tracking-tight mb-4">When to use fixtures</h2>
      <ul className="space-y-2 mb-8 text-sm text-white/40">
        <li className="flex gap-2"><span className="text-white/20">-</span> Components that fetch data from APIs</li>
        <li className="flex gap-2"><span className="text-white/20">-</span> Components behind authentication walls</li>
        <li className="flex gap-2"><span className="text-white/20">-</span> Components that use dynamic routing params</li>
        <li className="flex gap-2"><span className="text-white/20">-</span> Components with user-specific content</li>
      </ul>

      <h2 className="text-xl font-bold tracking-tight mb-4">Fixture tips</h2>
      <ul className="space-y-2 mb-8 text-sm text-white/40">
        <li className="flex gap-2"><span className="text-white/20">-</span> Use realistic text lengths (affects bone widths)</li>
        <li className="flex gap-2"><span className="text-white/20">-</span> Include all UI elements that should become bones</li>
        <li className="flex gap-2"><span className="text-white/20">-</span> Use placeholder images with correct dimensions</li>
      </ul>
    </article>
  );
}
